import { Type } from "@sinclair/typebox";
import { Nullable } from "./type-util";
export const ROLES = {
    uploader: "https://authority.nrk.no/role/N61",
    personInImage: "https://authority.nrk.no/role/N09",
    photographer: "https://authority.nrk.no/role/V48",
    supplier: "https://authority.nrk.no/role/V722",
};
export const DOMAINS = {
    promotional: "http://authority.nrk.no/datadictionary/promotionalImage",
    editorial: "http://authority.nrk.no/datadictionary/editorialImage",
    internal: "http://authority.nrk.no/datadictionary/internalImage",
};
export const AGENTS = {
    NRK: "http://authority.nrk.no/agent/0e3d2d26-bb2f-408e-af04-93aedf353895",
    NTB: "http://authority.nrk.no/agent/ee04ddb5-bbf4-4106-8b4a-1b5d5b75c3f6",
};
export const ContactSchema = Type.Object({
    title: Nullable(Type.String()),
    resId: Type.Optional(Type.String()),
}, { title: "Contact", $id: "Contact" });
export const DetectedFaceSchema = Type.Object({
    faceId: Type.String(),
    faceRectangle: Type.Object({
        top: Type.Number(),
        left: Type.Number(),
        width: Type.Number(),
        height: Type.Number(),
    }),
    faceAttributes: Type.Object({
        qualityForRecognition: Type.Union([
            Type.Literal("low"),
            Type.Literal("medium"),
            Type.Literal("high"),
        ]),
    }),
}, { title: "Detected face" });
export const SuggestedPersonFromFaceSchema = Type.Object({
    person: Type.Optional(Type.Object({
        name: Type.String(),
        uri: Type.String(),
        personId: Type.String(),
        confidence: Type.Number(),
    })),
});
export const DetectedFaceWithPersonSchema = Type.Composite([
    DetectedFaceSchema,
    SuggestedPersonFromFaceSchema,
]);
const AnyContributorSchema = Type.Object({
    contact: Type.Ref(ContactSchema),
    resId: Type.Optional(Type.String()),
});
export const UploaderSchema = Type.Composite([
    AnyContributorSchema,
    Type.Object({ role: Type.Object({ resId: Type.Literal(ROLES.uploader) }) }),
], { description: "Person who uploaded image", $id: "Uploader", title: "Uploader" });
export const PhotographerSchema = Type.Composite([
    AnyContributorSchema,
    Type.Object({ role: Type.Object({ resId: Type.Literal(ROLES.photographer) }) }),
], { description: "Person who took image", $id: "Photographer", title: "Photographer" });
export const SupplierSchema = Type.Composite([
    AnyContributorSchema,
    Type.Object({ role: Type.Object({ resId: Type.Literal(ROLES.supplier) }) }),
], { description: "Supplier of image", $id: "Supplier", title: "Supplier" });
export const PersonInImageSchema = Type.Composite([
    AnyContributorSchema,
    Type.Object({
        role: Type.Object({ resId: Type.Literal(ROLES.personInImage) }),
        detectedFace: Type.Optional(DetectedFaceSchema),
    }),
], { description: "Person in image", $id: "PersonInImage", title: "PersonInImage" });
export const ContributorSchema = Type.Union([Type.Ref(UploaderSchema), Type.Ref(PhotographerSchema), Type.Ref(PersonInImageSchema)], {
    $id: "Contributor",
    title: "Contributor",
    description: "People in front of or behind camera",
});
export function isPersonInImage(contributor) {
    return contributor.role.resId === "https://authority.nrk.no/role/N09";
}
export function isUploader(contributor) {
    return contributor.role.resId === ROLES.uploader;
}
export function isPhotographer(contributor) {
    return contributor.role.resId === ROLES.photographer;
}
// The Rights types and helper functions below have been copied from Potion's MDB client (with minor adjustments)
/** Derived rights marker for search and display */
export const RightsMarkerSchema = Type.Union([
    Type.Literal("Restricted"),
    Type.Literal("Free"),
    Type.Literal("Unknown"),
]);
export const PartySchema = Type.Object({
    contact: Type.Ref(ContactSchema),
}, { title: "Party", $id: "Party" });
export const RightsSchema = Type.Object({
    note: Type.Optional(Nullable(Type.String())),
    contractualParty: Type.Optional(Type.Ref(PartySchema)),
    rightsHolder: Type.Optional(Type.Ref(PartySchema)),
    credit: Type.Optional(Nullable(Type.String())),
}, {
    $id: "Rights",
    title: "Rights",
    description: "Describes rights for image",
});
export const isNrkRightsHolder = (rights) => {
    var _a;
    const contact = (_a = rights === null || rights === void 0 ? void 0 : rights.rightsHolder) === null || _a === void 0 ? void 0 : _a.contact;
    return (contact === null || contact === void 0 ? void 0 : contact.resId) === AGENTS.NRK || (contact === null || contact === void 0 ? void 0 : contact.title) === "NRK";
};
export const isCopyright = (rights) => rights && !isNrkRightsHolder(rights);
//IPTC DigitalSourceType NRK internal categorization.
//For example, digitalSourceTypesDerivedAsFotografi contains the digitalSourceTypes we derive to Fotografi
export const digitalSourceTypesDerivedAsFotografi = [
    "http://cv.iptc.org/newscodes/digitalsourcetype/digitalCapture",
    "http://cv.iptc.org/newscodes/digitalsourcetype/computationalCapture",
    "http://cv.iptc.org/newscodes/digitalsourcetype/negativeFilm",
    "http://cv.iptc.org/newscodes/digitalsourcetype/positiveFilm",
    "http://cv.iptc.org/newscodes/digitalsourcetype/print",
    "http://cv.iptc.org/newscodes/digitalsourcetype/humanEdits",
    "http://cv.iptc.org/newscodes/digitalsourcetype/algorithmicallyEnhanced",
    "http://cv.iptc.org/newscodes/digitalsourcetype/compositeCapture",
    "http://cv.iptc.org/newscodes/digitalsourcetype/minorHumanEdits",
];
export const digitalSourceTypesDerivedAsGrafikk = [
    "http://cv.iptc.org/newscodes/digitalsourcetype/dataDrivenMedia",
    "http://cv.iptc.org/newscodes/digitalsourcetype/digitalArt",
    "http://cv.iptc.org/newscodes/digitalsourcetype/softwareImage",
    "http://cv.iptc.org/newscodes/digitalsourcetype/composite",
];
export const digitalSourceTypesDerivedAsIllustrasjon = [
    "http://cv.iptc.org/newscodes/digitalsourcetype/digitalCreation",
];
export const digitalSourceTypesDerivedAsFaksimile = [
    "http://cv.iptc.org/newscodes/digitalsourcetype/screenCapture",
    "https://authority.nrk.no/datadictionary/digitalFacsimile",
];
export const digitalSourceTypesDerivedAsKIGenerert = [
    "http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia",
    "http://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia",
    "http://cv.iptc.org/newscodes/digitalsourcetype/algorithmicMedia",
    "http://cv.iptc.org/newscodes/digitalsourcetype/virtualRecording",
    "http://cv.iptc.org/newscodes/digitalsourcetype/compositeSynthetic",
];
// Dette er de vanligste/enkleste verdiene for hver kategori.
export const digitalSourceTypePhotography = "http://cv.iptc.org/newscodes/digitalsourcetype/digitalCapture";
export const digitalSourceTypeFaksimile = "http://cv.iptc.org/newscodes/digitalsourcetype/screenCapture";
export const digitalSourceTypeGrafikk = "http://cv.iptc.org/newscodes/digitalsourcetype/composite";
export const digitalSourceTypeIllustrasjon = "http://cv.iptc.org/newscodes/digitalsourcetype/digitalCreation";
export const digitalSourceTypeKIGenerert = "http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia";
/** Derived image type */
export const commonImageTypes = [
    digitalSourceTypeIllustrasjon,
    digitalSourceTypeKIGenerert,
    digitalSourceTypeFaksimile,
    digitalSourceTypeGrafikk,
    digitalSourceTypePhotography,
];
export const digitalSourceTypeGroupLabels = [
    "Fotografi",
    "Faksimile",
    "Grafikk",
    "Illustrasjon",
    "KI-generert",
];
export const ImageTypeSchema = Type.Union(commonImageTypes.map((type) => Type.Literal(type)), {
    $id: "ImageTypeSchema",
});
export const DigitalSourceTypeSchema = Type.Union([
    ...digitalSourceTypesDerivedAsFotografi,
    ...digitalSourceTypesDerivedAsGrafikk,
    ...digitalSourceTypesDerivedAsIllustrasjon,
    ...digitalSourceTypesDerivedAsFaksimile,
    ...digitalSourceTypesDerivedAsKIGenerert,
].map((type) => Type.Literal(type)), {
    $id: "DigitalSourceTypeSchema",
});
const digitalSourceTypeToGroupLabel = new Map([
    ...digitalSourceTypesDerivedAsFotografi.map((t) => [t, "Fotografi"]),
    ...digitalSourceTypesDerivedAsGrafikk.map((t) => [t, "Grafikk"]),
    ...digitalSourceTypesDerivedAsIllustrasjon.map((t) => [t, "Illustrasjon"]),
    ...digitalSourceTypesDerivedAsFaksimile.map((t) => [t, "Faksimile"]),
    ...digitalSourceTypesDerivedAsKIGenerert.map((t) => [t, "KI-generert"]),
]);
export function deriveSimplifiedDigitalSourceType(digitalSourceType) {
    if (!digitalSourceType)
        return undefined;
    return digitalSourceTypeToGroupLabel.get(digitalSourceType);
}
export const NrkDataSchema = Type.Object({
    contributors: Type.Optional(Type.Array(Type.Ref(ContributorSchema))),
    rights: Type.Optional(Nullable(Type.Ref(RightsSchema))),
}, { description: "NRK specific fields that with nested data structure" });
/** This array allows us to handle these fields differently when transforming data */
export const NRK_FIELDS = Object.keys(NrkDataSchema.properties);
//# sourceMappingURL=nrk-data.js.map